import { ActiveDeviceStatus } from "../device-tokens/schema";

const DEVICE_TOKENS_PATH = "/api/v1/device-tokens";

export interface DeviceTokenApiOptions {
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Root directory base URL (e.g. "https://a3s-int.visioni.network") — NOT a realm URL. */
  directoryBaseUrl: string | (() => Promise<string>);
  /** Optional per-installation id to persist with token records for diagnostics. */
  deviceId?: string | (() => string | Promise<string> | undefined);
}

export interface DeviceTokenApi {
  isRegistered(token: string): Promise<{ registered: boolean; ttl?: number }>;
  /** Whether any of the caller's devices currently holds an unexpired push registration — for clients with no token of their own (web). */
  hasActiveDevice(): Promise<boolean>;
  registerToken(token: string, platform: "ios" | "android"): Promise<void>;
  unregisterToken(token: string): Promise<void>;
}

export function createDeviceTokenApi({
  authenticatedFetch,
  directoryBaseUrl,
  deviceId,
}: DeviceTokenApiOptions): DeviceTokenApi {
  async function baseUrl(): Promise<string> {
    const url = typeof directoryBaseUrl === "function" ? await directoryBaseUrl() : directoryBaseUrl;
    return `${url}${DEVICE_TOKENS_PATH}`;
  }
  async function resolveDeviceId(): Promise<string | undefined> {
    const value = typeof deviceId === "function" ? await Promise.resolve(deviceId()) : deviceId;
    return typeof value === "string" && value.length > 0 ? value : undefined;
  }

  return {
    async isRegistered(token: string): Promise<{ registered: boolean; ttl?: number }> {
      const url = `${await baseUrl()}?token=${encodeURIComponent(token)}`;
      const response = await authenticatedFetch(url, { method: "GET" });
      if (response.status === 404) return { registered: false };
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check token registration: ${response.status.toString()} ${errorText}`);
      }
      return (await response.json()) as { registered: boolean; ttl?: number };
    },

    async hasActiveDevice(): Promise<boolean> {
      const response = await authenticatedFetch(await baseUrl(), { method: "GET" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check for an active device: ${response.status.toString()} ${errorText}`);
      }
      return ActiveDeviceStatus.parse(await response.json()).hasActiveDevice;
    },

    async registerToken(token: string, platform: "ios" | "android"): Promise<void> {
      const url = await baseUrl();
      const currentDeviceId = await resolveDeviceId();
      const response = await authenticatedFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, platform, ...(currentDeviceId ? { deviceId: currentDeviceId } : {}) }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to register token: ${response.status.toString()} ${errorText}`);
      }
    },

    async unregisterToken(token: string): Promise<void> {
      const url = await baseUrl();
      const response = await authenticatedFetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (response.status === 404) return;
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to unregister token: ${response.status.toString()} ${errorText}`);
      }
    },
  };
}
